import { computed, onBeforeUnmount, ref, shallowRef } from 'vue';
import {
  calculatePaymentQuote,
  changePhotoQuantity,
  formatMoney,
  getSelectionItems,
} from '@/domain/selection';
import type {
  EntrScreen,
  EntrSession,
  PaymentQuote,
  PhotoSelection,
  PriceConfig,
} from '@/domain/types';
import {
  addBreadcrumb,
  captureException,
  captureMessage,
  clearSentrySession,
  endSentrySession,
  setSentryScreen,
  startSentrySession,
} from '@/telemetry/sentry';
import { MOCK_PRICES } from '@/transport/mockData';
import { createEntrRuntime } from '@/transport/runtime';
import type { EntrRuntime } from '@/transport/types';
import type { AttractResetReason, ControllerSheet } from './controllerTypes';
import { createShowError } from './controllerUtils';
import { createPrintedCoverageWritebackQueue } from './printedCoverageWritebackQueue';
import { useAttractGroups } from './useAttractGroups';
import { useCheckoutFlow } from './useCheckoutFlow';
import { useIdleReset } from './useIdleReset';
import { useSessionLookup } from './useSessionLookup';

const ATTRACT_GROUPS_PAGE_SIZE = 12;
// An idle kiosk shows the attract list all day, thus a short interval sends
// many requests. A new group shows at most 30 s late, and the list reloads at
// once when the kiosk goes back to the attract screen.
const ATTRACT_GROUPS_REFRESH_MS = 30_000;
const IDLE_TIMEOUT_MS = 90_000;
const IDLE_PROMPT_MS = 30_000;
const TICKET_NOT_FOUND_DISMISS_MS = 6000;
const PHOTOS_NOT_READY_DISMISS_MS = 7000;
const LOOKUP_NETWORK_ERROR_DISMISS_MS = 10_000;

// Support code shown to visitors on the exhausted-retry error sheet (so they
// can flag staff), NOT the payment override — that is voucherCode, scanned as
// a QR. Falls back to a per-build random when VITE_ENTR_HELP_CODE is unset.
const BUILD_DEFAULT_HELP_CODE = createBuildDefaultHelpCode();

interface EntrAppControllerOptions {
  onAttractReset?: () => void;
  runtime?: EntrRuntime;
  idleTimeoutMs?: number;
  idlePromptMs?: number;
  helpCode?: string;
  voucherCode?: string;
  staffPaymentBypassEnabled?: boolean;
  priceConfig?: () => PriceConfig;
  paymentEnabled?: () => boolean;
}

// Terminal payment is on by default; set VITE_ENTR_PAYMENT_ENABLED='false'
// as a build-time kill-switch to force every visitor straight to printing
// (e.g. a venue whose Stripe Connect onboarding isn't finished). In strict
// runtime config the backend kiosk's is_stripe_terminal_payment still gates
// per kiosk — see isRuntimePaymentEnabled in useEntrBoot. Implemented as a
// function (not module-const) so vitest can stub the env per test.
function isPaymentEnabled(): boolean {
  return import.meta.env.VITE_ENTR_PAYMENT_ENABLED !== 'false';
}

// Opening-night kill-switch. ENTR's /api/photo_groups upstream wasn't
// wired in time for 2026-05-22 — without this guard the Attract screen
// would call listGroups() which falls through to mock data, rendering
// fake "Robert Kroner" groups in production. Off ⇒ the controller
// skips listGroups entirely and the screen renders as scan-QR-only.
function isGroupBrowseEnabled(): boolean {
  return import.meta.env.VITE_ENTR_GROUP_BROWSE_ENABLED === 'true';
}

function isGroupBrowsePollingEnabled(): boolean {
  return import.meta.env.VITE_ENTR_GROUP_BROWSE_POLLING_ENABLED !== 'false';
}

export function useEntrAppController(options: EntrAppControllerOptions = {}) {
  const runtime = options.runtime ?? createEntrRuntime();
  const printedCoverageWritebacks = createPrintedCoverageWritebackQueue(
    runtime.data.recordPhotoPrinted,
    { addBreadcrumb, captureException, captureMessage }
  );
  const screen = shallowRef<EntrScreen>('attract');
  const isBusy = ref(false);
  const session = shallowRef<EntrSession | null>(null);
  const selection = shallowRef<PhotoSelection>({});
  const sheet = shallowRef<ControllerSheet>(null);
  const toastKey = ref('');
  const paymentState = ref<'idle' | 'waiting' | 'failed'>('idle');
  const printState = ref<'idle' | 'printing' | 'failed'>('idle');
  const isStarted = ref(false);
  const timers = new Set<number>();
  const helpCode =
    options.helpCode ??
    import.meta.env.VITE_ENTR_HELP_CODE ??
    BUILD_DEFAULT_HELP_CODE;
  // Opaque token the staff voucher QR must equal to override payment. No build
  // default — an unset code leaves the override inert (empty never matches),
  // so a missing secret can't accidentally hand out free prints.
  const voucherCode =
    options.voucherCode ?? import.meta.env.VITE_ENTR_VOUCHER_CODE ?? '';
  const staffPaymentBypassEnabled =
    options.staffPaymentBypassEnabled ??
    import.meta.env.VITE_ENTR_STAFF_PAYMENT_BYPASS === 'true';
  const priceConfig = computed(() => options.priceConfig?.() ?? MOCK_PRICES);
  const paymentEnabled = computed(
    () => options.paymentEnabled?.() ?? isPaymentEnabled()
  );
  let isControllerRunning = false;
  let sheetSequence = 0;
  let sheetAutoDismissTimer: number | null = null;
  const showError = createShowError(setSheet);
  const showTicketNotFound = (): void =>
    showError('errors.ticketNotFoundTitle', 'errors.ticketNotFoundBody', {
      autoDismissMs: TICKET_NOT_FOUND_DISMISS_MS,
    });
  const showLookupNetworkError = (retry?: () => void): void =>
    showError('errors.networkErrorTitle', 'errors.networkErrorBody', {
      retry,
      autoDismissMs: LOOKUP_NETWORK_ERROR_DISMISS_MS,
    });

  const {
    groups,
    loadGroups,
    startPolling: startAttractGroupsPolling,
    stopPolling: stopAttractGroupsPolling,
    onEnteredAttract,
    onVisibilityChange: onAttractVisibilityChange,
  } = useAttractGroups({
    listGroups: runtime.data.listGroups,
    pageSize: ATTRACT_GROUPS_PAGE_SIZE,
    refreshMs: ATTRACT_GROUPS_REFRESH_MS,
    isControllerRunning: () => isControllerRunning,
    getScreen: () => screen.value,
    isBrowseEnabled: isGroupBrowseEnabled,
    isPollingEnabled: isGroupBrowsePollingEnabled,
    setBusy: (value) => {
      isBusy.value = value;
    },
    showGroupsLoadError: (retry) =>
      showError('errors.groupsLoadTitle', 'errors.groupsLoadBody', { retry }),
    clearGroupsLoadError: () => {
      if (
        sheet.value?.kind === 'error' &&
        sheet.value.titleKey === 'errors.groupsLoadTitle'
      ) {
        setSheet(null);
      }
    },
    onAttractReset: options.onAttractReset,
    observer: {
      addBreadcrumb,
      captureException,
    },
  });

  const { selectGroup, handleTicketCode, invalidateLookup } = useSessionLookup({
    getSessionByGroup: runtime.data.getSessionByGroup,
    getSessionByTicketCode: runtime.data.getSessionByTicketCode,
    canHandleTicketCode: () =>
      screen.value === 'scan-ticket' || screen.value === 'attract',
    setBusy: (value) => {
      isBusy.value = value;
    },
    onSessionResult: handleSessionResult,
    showTicketNotFoundError: showTicketNotFound,
    showNetworkError: showLookupNetworkError,
    observer: {
      addBreadcrumb,
      captureException,
    },
  });

  const allPhotos = computed(() => {
    const current = session.value;
    if (!current) return [];
    return [...current.groupPhotos, ...current.portraits];
  });

  const selectionItems = computed(() =>
    getSelectionItems(allPhotos.value, selection.value, priceConfig.value)
  );

  const quote = computed<PaymentQuote>(() =>
    calculatePaymentQuote(
      selectionItems.value,
      session.value?.voucher ?? { kind: 'none' },
      priceConfig.value
    )
  );

  const checkoutFlow = useCheckoutFlow({
    runtime,
    session,
    quote,
    selectionItems,
    paymentEnabled,
    paymentState,
    printState,
    staffPaymentBypassEnabled,
    helpCode,
    voucherCode,
    getScreen: () => screen.value,
    setScreen,
    setSheet,
    resetToAttract,
    scheduleResetToAttract,
    printedCoverageWritebacks,
    observer: {
      addBreadcrumb,
      captureException,
      captureMessage,
    },
  });

  function setScreen(nextScreen: EntrScreen): void {
    const previousScreen = screen.value;
    if (previousScreen === 'attract' && nextScreen !== 'attract') {
      stopAttractGroupsPolling();
    }
    if (previousScreen === 'printing' && nextScreen !== 'printing') {
      checkoutFlow.onLeavingPrinting();
    }
    screen.value = nextScreen;
    setSentryScreen(nextScreen);
    if (nextScreen === 'attract' && previousScreen !== 'attract') {
      onEnteredAttract();
    }
  }

  async function start(): Promise<void> {
    isControllerRunning = true;
    runtime.scanner.start((scannedCode) => {
      // On the payment screen a scan is a staff voucher override, not a
      // ticket; only fall through to ticket lookup when it isn't consumed.
      const voucher = checkoutFlow.tryVoucherScan(scannedCode);
      if (voucher === 'accepted') return;
      if (voucher === 'rejected') {
        showVoucherInvalidToast();
        return;
      }
      void handleTicketCode(scannedCode);
    });
    isStarted.value = true;
    printedCoverageWritebacks.drain();
    document.addEventListener('visibilitychange', onAttractVisibilityChange);
    if (isGroupBrowseEnabled()) {
      await loadGroups(1);
      startAttractGroupsPolling();
    }
  }

  function stop(): void {
    isControllerRunning = false;
    document.removeEventListener('visibilitychange', onAttractVisibilityChange);
    stopAttractGroupsPolling();
    printedCoverageWritebacks.stop();
    clearTimers();
    checkoutFlow.stop();
    runtime.scanner.stop();
    isStarted.value = false;
    if (session.value) {
      endSentrySession('abnormal');
      clearSentrySession();
    }
  }

  function handleSessionResult(
    result: Awaited<ReturnType<typeof runtime.data.getSessionByGroup>>,
    retry?: () => void
  ): void {
    if (!result.ok) {
      if (result.reason === 'photos-not-ready') {
        showError('errors.photosNotReadyTitle', 'errors.photosNotReadyBody', {
          autoDismissMs: PHOTOS_NOT_READY_DISMISS_MS,
        });
        return;
      }
      if (result.reason === 'network-error') {
        showLookupNetworkError(retry);
        return;
      }

      showTicketNotFound();
      return;
    }

    clearTransientSheet();
    session.value = result.value;
    selection.value = {};
    checkoutFlow.resetForAttract();
    startSentrySession({
      sessionId: result.value.id,
      groupId: result.value.group.id,
      source: result.value.source,
      photoCount:
        result.value.groupPhotos.length + result.value.portraits.length,
      voucherKind: result.value.voucher.kind,
    });
    setScreen('select-photos');
  }

  function changeQuantity(photoId: string, delta: number): void {
    selection.value = changePhotoQuantity(selection.value, photoId, delta);
  }

  async function resetToAttract(
    reason: AttractResetReason = 'manual'
  ): Promise<void> {
    addBreadcrumb('entr.reset', 'Reset to attract', { reason });
    // It does not reject: it captures a cancel failure itself.
    void checkoutFlow.cancelActivePaymentForReset();

    const hadSession = Boolean(session.value);
    invalidateLookup();
    clearTimers();
    checkoutFlow.resetForAttract();
    session.value = null;
    selection.value = {};
    isBusy.value = false;
    setSheet(null);
    if (hadSession) endSentrySession('exited');
    clearSentrySession();
    setScreen('attract');
  }

  function goBack(): void {
    if (screen.value === 'scan-ticket') {
      invalidateLookup();
      isBusy.value = false;
      setScreen('attract');
      return;
    }
    if (screen.value === 'select-photos') {
      void resetToAttract('navigation');
      return;
    }
    checkoutFlow.goBackFromPayment();
  }

  function openScan(): void {
    if (!isStarted.value) {
      addBreadcrumb('entr.scan', 'scan open ignored before runtime start');
      return;
    }
    setScreen('scan-ticket');
  }

  function closeSheet(): void {
    const currentSheet = sheet.value;
    setSheet(null);
    // Without the sheet, the visitor can select the back button of the payment
    // screen. A new checkout then starts with no failed attempts.
    if (currentSheet?.kind === 'error' && currentSheet.exit) {
      currentSheet.exit();
      return;
    }
    if (
      currentSheet?.kind === 'error' &&
      currentSheet.titleKey === 'errors.paymentSessionUnavailableTitle'
    ) {
      void resetToAttract('session-unavailable');
      return;
    }
    const isFailedPrintSheet =
      currentSheet?.kind === 'error' &&
      (currentSheet.titleKey === 'errors.printFailedTitle' ||
        currentSheet.titleKey === 'errors.printTimeoutTitle');
    if (
      isFailedPrintSheet &&
      screen.value === 'printing' &&
      printState.value === 'failed'
    ) {
      void resetToAttract('print-failure');
    }
  }

  function callHelp(): void {
    toastKey.value = 'common.helpCalled';
    addBreadcrumb('entr.help', 'Help requested manually');
    addTimer(() => {
      toastKey.value = '';
    }, 2400);
  }

  function showVoucherInvalidToast(): void {
    toastKey.value = 'payment.voucherInvalid';
    addBreadcrumb('entr.payment', 'Voucher scan invalid toast shown');
    addTimer(() => {
      toastKey.value = '';
    }, 2400);
  }

  function setSheet(nextSheet: ControllerSheet | null): void {
    sheetSequence += 1;
    clearSheetAutoDismissTimer();
    sheet.value = nextSheet;
    if (nextSheet?.kind !== 'error' || !nextSheet.autoDismissMs) return;

    const currentSheetSequence = sheetSequence;
    const timer = window.setTimeout(() => {
      timers.delete(timer);
      if (sheetAutoDismissTimer === timer) {
        sheetAutoDismissTimer = null;
      }
      if (sheetSequence === currentSheetSequence) {
        setSheet(null);
      }
    }, nextSheet.autoDismissMs);
    sheetAutoDismissTimer = timer;
    timers.add(timer);
  }

  function clearSheetAutoDismissTimer(): void {
    if (sheetAutoDismissTimer === null) return;
    window.clearTimeout(sheetAutoDismissTimer);
    timers.delete(sheetAutoDismissTimer);
    sheetAutoDismissTimer = null;
  }

  function clearTransientSheet(): void {
    if (sheet.value?.kind === 'error' && sheet.value.autoDismissMs) {
      setSheet(null);
    }
  }

  function addTimer(callback: () => void, delayMs: number): void {
    const timer = window.setTimeout(() => {
      timers.delete(timer);
      callback();
    }, delayMs);
    timers.add(timer);
  }

  function scheduleResetToAttract(
    reason: AttractResetReason,
    delayMs: number,
    shouldReset: () => boolean = () => true
  ): () => void {
    addBreadcrumb('entr.reset', 'Reset scheduled', {
      reason,
      delay_ms: delayMs,
      ...resetState(),
    });
    const timer = window.setTimeout(() => {
      timers.delete(timer);
      if (shouldReset()) {
        void resetToAttract(reason);
        return;
      }

      addBreadcrumb('entr.reset', 'Reset skipped', { reason, ...resetState() });
    }, delayMs);
    timers.add(timer);

    return () => {
      window.clearTimeout(timer);
      timers.delete(timer);
      addBreadcrumb('entr.reset', 'Scheduled reset canceled', {
        reason,
        ...resetState(),
      });
    };
  }

  function resetState(): Record<string, unknown> {
    return {
      screen: screen.value,
      payment_state: paymentState.value,
      print_state: printState.value,
      has_session: Boolean(session.value),
    };
  }

  function clearTimers(): void {
    for (const timer of timers) window.clearTimeout(timer);
    timers.clear();
    sheetAutoDismissTimer = null;
  }

  onBeforeUnmount(stop);

  const idleReset = useIdleReset({
    timeoutMs: options.idleTimeoutMs ?? IDLE_TIMEOUT_MS,
    promptMs: options.idlePromptMs ?? IDLE_PROMPT_MS,
    // While the reader waits for a card, the visitor uses the reader, not the
    // screen, and the card window sets the time limit. An idle reset at 90 s
    // cancels the reader before that window ends, thus the timeout text never
    // shows. The B2C kiosk pauses its idle watcher for the same reason.
    isActive: () =>
      screen.value === 'scan-ticket' ||
      screen.value === 'select-photos' ||
      (screen.value === 'card-payment' && !checkoutFlow.isWaitingForCard()),
    // `printing` is intentionally excluded — the print screen has its
    // own PRINT_HANG_TIMEOUT_MS watchdog that owns abort + farewell,
    // and a second idle-reset firing would race the print-completion
    // path (see #410 audit: the bug-hunter's "incomplete #378" was
    // wrong — the two watchdogs serve the same job, not different jobs).
    onTimeout: () => resetToAttract('idle-timeout'),
  });

  return {
    screen,
    groups,
    isBusy,
    session,
    selection,
    selectionItems,
    quote,
    sheet,
    toastKey,
    paymentState,
    printState,
    idlePromptVisible: idleReset.isPromptVisible,
    idleSecondsRemaining: idleReset.secondsRemaining,
    helpCode,
    staffPaymentBypassEnabled,
    formattedAmountDue: computed(() =>
      formatMoney(quote.value.amountDueCents, quote.value.currency)
    ),
    start,
    stop,
    loadGroups,
    selectGroup,
    handleTicketCode,
    changeQuantity,
    continueFromSelection: checkoutFlow.continueFromSelection,
    continueIdleSession: idleReset.continueSession,
    resetToAttract,
    goBack,
    openScan,
    closeSheet,
    callHelp,
    scanner: runtime.scanner,
    isStarted,
  };
}

function createBuildDefaultHelpCode(): string {
  return Math.floor(1000 + Math.random() * 9000).toString();
}
